// Shared by the company-level and location-level Limits & Controls sections.
// Mirrors the API validation added for GAT-7592 on UpdateLocationLimitsRequest
// and CompanyRequest, so a value the API would refuse is caught here with the
// same rule instead of coming back as a failed save.
//
// The company section saves through the generic /settings/setting endpoint,
// which does not run that validation, so for that screen this is the only
// guard. Keep these in sync with the API if its bounds change.
export const MIN_SHIFT_DURATION_HOURS = 1;
export const MAX_SHIFT_DURATION_HOURS = 64;

// 0 is meaningful: the API reads it as "no limit" rather than "no signups".
export const MIN_CONTINUOUS_SIGNUP_HOURS = 0;
export const MAX_CONTINUOUS_SIGNUP_HOURS = 128;

export interface ShiftLimitValues {
	defaultDuration: number;
	minDuration: number;
	maxDuration: number;
	maxContinuousHours: number;
}

export type ShiftLimitErrors = Partial<Record<keyof ShiftLimitValues, string>>;

const isOutOfRange = (value: number, min: number, max: number) =>
	!Number.isFinite(value) || value < min || value > max;

const SHIFT_DURATION_RANGE_MESSAGE =
	`Must be between ${MIN_SHIFT_DURATION_HOURS} and ` +
	`${MAX_SHIFT_DURATION_HOURS} hours`;

// Same order as the API: per-field ranges first, then min <= max, then the
// default inside that range. Ordering is only checked once every range passes,
// and the default only once min <= max, so a field never shows two messages.
export const getShiftLimitErrors = (
	values: ShiftLimitValues
): ShiftLimitErrors => {
	const errors: ShiftLimitErrors = {};
	const defaultDuration = Number(values.defaultDuration);
	const minDuration = Number(values.minDuration);
	const maxDuration = Number(values.maxDuration);
	const maxContinuousHours = Number(values.maxContinuousHours);

	if (
		isOutOfRange(
			maxContinuousHours,
			MIN_CONTINUOUS_SIGNUP_HOURS,
			MAX_CONTINUOUS_SIGNUP_HOURS
		)
	) {
		errors.maxContinuousHours =
			`Must be between ${MIN_CONTINUOUS_SIGNUP_HOURS} and ` +
			`${MAX_CONTINUOUS_SIGNUP_HOURS} hours (0 means no limit)`;
	}

	const durations = {
		defaultDuration,
		minDuration,
		maxDuration,
	};
	let isEveryDurationInRange = true;
	(Object.keys(durations) as (keyof typeof durations)[]).forEach((key) => {
		if (
			isOutOfRange(
				durations[key],
				MIN_SHIFT_DURATION_HOURS,
				MAX_SHIFT_DURATION_HOURS
			)
		) {
			errors[key] = SHIFT_DURATION_RANGE_MESSAGE;
			isEveryDurationInRange = false;
		}
	});
	if (!isEveryDurationInRange) return errors;

	if (minDuration > maxDuration) {
		errors.minDuration = "Cannot exceed the max. shift duration";
		errors.maxDuration = "Cannot be less than the min. shift duration";
		return errors;
	}

	if (defaultDuration < minDuration || defaultDuration > maxDuration) {
		errors.defaultDuration = "Must be between the min. and max. shift duration";
	}

	return errors;
};

const FIELD_LABELS: Record<keyof ShiftLimitValues, string> = {
	defaultDuration: "Default shift duration",
	minDuration: "Min. shift duration",
	maxDuration: "Max. shift duration",
	maxContinuousHours: "Maximum continuous signup hours",
};

// The first error as a sentence naming its field, for a save-time snackbar.
export const getFirstShiftLimitError = (errors: ShiftLimitErrors): string => {
	const key = (Object.keys(FIELD_LABELS) as (keyof ShiftLimitValues)[]).find(
		(field) => errors[field]
	);
	if (!key) return "";
	return `${FIELD_LABELS[key]}: ${errors[key]}.`;
};
